import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiLock, FiSmartphone } from 'react-icons/fi';
import { requestAccountRecovery, resetAccountPassword, validateRecoveryIdentity, validateRecoveryPassword, verifyAccountRecovery } from './accountRecoveryApi';
import './AccountRecovery.scss';

const remainingSeconds = (deadline, now) => Math.max(0, Math.ceil((deadline - now) / 1000));
const clockText = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export default function AccountRecovery({ mode = 'id' }) {
  const purpose = mode === 'password' ? 'password' : 'id';
  return <RecoveryFlow key={purpose} purpose={purpose} />;
}

function RecoveryFlow({ purpose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialId = typeof location.state?.recoveredLoginId === 'string' ? location.state.recoveredLoginId : '';
  const [identity, setIdentity] = useState({ name: '', phone: '', id: initialId });
  const identityRef = useRef(identity);
  const [challenge, setChallenge] = useState(null);
  const [reset, setReset] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const generation = useRef(0);
  const lock = useRef(null);
  const mounted = useRef(false);
  const errorRef = useRef(null);
  const stepRef = useRef(null);
  const title = purpose === 'password' ? '비밀번호 재설정' : '아이디 찾기';
  const challengeSeconds = challenge ? remainingSeconds(challenge.expiresAt, now) : 0;
  const resetSeconds = reset ? remainingSeconds(reset.expiresAt, now) : 0;
  const retrySeconds = remainingSeconds(retryAt, now);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      lock.current = null;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [challenge, reset, retryAt]);

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { stepRef.current?.focus(); }, [challenge, reset, accounts]);

  const isCurrent = (request) => mounted.current && generation.current === request;
  const startRequest = (type) => {
    if (lock.current !== null) return null;
    const request = ++generation.current;
    lock.current = request;
    setBusy(type);
    setError('');
    return request;
  };
  const finishRequest = (request) => {
    if (isCurrent(request)) { lock.current = null; setBusy(''); }
  };
  const showFailure = (request, failure) => {
    if (!isCurrent(request)) return;
    setError(failure.message);
    if (failure.retryAfter) {
      const receivedAt = Date.now();
      setNow(receivedAt);
      setRetryAt(receivedAt + failure.retryAfter * 1000);
    }
  };

  const changeIdentity = (event) => {
    const { name, value } = event.target;
    identityRef.current = { ...identityRef.current, [name]: value };
    setIdentity(identityRef.current);
    generation.current += 1;
    lock.current = null;
    setBusy('');
    setChallenge(null);
    setReset(null);
    setAccounts(null);
    setCode('');
    setPassword('');
    setConfirmation('');
    setError('');
  };

  const requestCode = async (event) => {
    event?.preventDefault();
    if (lock.current !== null || retryAt > Date.now()) return;
    const snapshot = { ...identityRef.current, purpose };
    const validation = validateRecoveryIdentity(snapshot);
    if (validation) { setError(validation); return; }
    const request = startRequest('request');
    if (request === null) return;
    setChallenge(null);
    setCode('');
    try {
      const result = await requestAccountRecovery(snapshot);
      if (!isCurrent(request)) return;
      const receivedAt = Date.now();
      setNow(receivedAt);
      setRetryAt(receivedAt + result.retryAfter * 1000);
      setChallenge({ challengeId: result.challengeId, expiresAt: receivedAt + result.expiresIn * 1000 });
    } catch (failure) { showFailure(request, failure); }
    finally { finishRequest(request); }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    if (lock.current !== null) return;
    if (!challenge || challenge.expiresAt <= Date.now()) { setError('인증 시간이 지났습니다. 인증번호를 다시 요청해 주세요.'); return; }
    if (!/^[0-9]{6}$/.test(code)) { setError('문자로 받은 인증번호 6자리를 입력해 주세요.'); return; }
    const request = startRequest('verify');
    if (request === null) return;
    try {
      const result = await verifyAccountRecovery({ challengeId: challenge.challengeId, code }, purpose);
      if (!isCurrent(request)) return;
      setChallenge(null);
      setCode('');
      if (purpose === 'password') {
        const receivedAt = Date.now();
        setNow(receivedAt);
        setReset({ resetToken: result.resetToken, expiresAt: receivedAt + result.expiresIn * 1000 });
      } else setAccounts(result.accounts);
    } catch (failure) { showFailure(request, failure); }
    finally { finishRequest(request); }
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    if (lock.current !== null) return;
    if (!reset || reset.expiresAt <= Date.now()) { setError('인증 시간이 지났습니다. 본인 확인을 다시 진행해 주세요.'); return; }
    const validation = validateRecoveryPassword(password, confirmation);
    if (validation) { setError(validation); return; }
    const request = startRequest('reset');
    if (request === null) return;
    try {
      await resetAccountPassword({ resetToken: reset.resetToken, password });
      if (!isCurrent(request)) return;
      setReset(null);
      setPassword('');
      setConfirmation('');
      navigate('/login', { replace: true, state: { passwordReset: true, recoveredLoginId: identityRef.current.id.trim() } });
    } catch (failure) { showFailure(request, failure); }
    finally { finishRequest(request); }
  };

  const restart = () => {
    if (lock.current !== null) return;
    generation.current += 1;
    setChallenge(null); setReset(null); setAccounts(null); setCode('');
    setPassword(''); setConfirmation(''); setError('');
  };
  const toLogin = (id) => navigate('/login', { state: { recoveredLoginId: id } });
  const indicator = <span className="recovery-spinner" aria-hidden="true" />;

  return <main className="account-recovery">
    <div className="recovery-shell">
      <header className="recovery-header">
        <div className="recovery-topbar">
          <button type="button" className="recovery-back" aria-label="로그인으로 돌아가기" onClick={() => navigate('/login')} disabled={busy === 'reset'}><FiArrowLeft aria-hidden="true" /></button>
          <span className="recovery-brand">AI K-EDU <span>TALK</span></span>
        </div>
        <h1>{title}</h1>
        <p>{purpose === 'password' ? '휴대전화로 본인 확인 후 새 비밀번호를 설정하세요.' : '가입한 이름과 휴대전화로 아이디를 확인하세요.'}</p>
      </header>

      <section className="recovery-code-card" aria-label="서비스 인증코드">
        <div><label htmlFor="recovery-cert-code">인증코드</label><span><FiLock aria-hidden="true" /> 고정</span></div>
        <input id="recovery-cert-code" value="FD2001" disabled />
      </section>

      {error && <p className="recovery-error" role="alert" tabIndex={-1} ref={errorRef}>{error}</p>}

      {accounts ? <section className="recovery-card recovery-result" aria-labelledby="recovery-result-title">
        <FiCheckCircle className="recovery-success-icon" aria-hidden="true" />
        <h2 id="recovery-result-title" tabIndex={-1} ref={stepRef}>아이디를 확인했습니다</h2>
        <p>확인한 아이디로 로그인해 주세요.</p>
        <ul className="recovery-accounts">{accounts.map(({ id }, index) => <li key={`${id}-${index}`}>
          <strong>{id}</strong><button type="button" className="recovery-primary" onClick={() => toLogin(id)}>로그인하기</button>
          <button type="button" className="recovery-link" onClick={() => navigate('/findPw', { state: { recoveredLoginId: id } })}>비밀번호 재설정</button>
        </li>)}</ul>
      </section> : reset ? <section className="recovery-card" aria-labelledby="recovery-reset-title">
        <h2 id="recovery-reset-title" tabIndex={-1} ref={stepRef}>새 비밀번호 설정</h2>
        <p className="recovery-hint">{identity.id} 계정의 새 비밀번호를 입력해 주세요.</p>
        <p className="recovery-timer">{resetSeconds ? `설정 가능 시간 ${clockText(resetSeconds)}` : '인증 시간이 만료되었습니다.'}</p>
        <form onSubmit={submitPassword} noValidate aria-busy={busy === 'reset'}>
          <fieldset disabled={Boolean(busy) || !resetSeconds}>
            <div className="recovery-field"><label htmlFor="recovery-password">새 비밀번호</label><input id="recovery-password" type="password" autoComplete="new-password" value={password} onChange={(event) => { setPassword(event.target.value); setError(''); }} maxLength={31} placeholder="6자 이상 입력해 주세요" /></div>
            <div className="recovery-field"><label htmlFor="recovery-password-confirm">새 비밀번호 확인</label><input id="recovery-password-confirm" type="password" autoComplete="new-password" value={confirmation} onChange={(event) => { setConfirmation(event.target.value); setError(''); }} maxLength={31} placeholder="한 번 더 입력해 주세요" /></div>
            <p className="recovery-hint">영문·숫자는 최대 31자까지 사용할 수 있습니다.</p>
            <button type="submit" className="recovery-primary">{busy === 'reset' ? <>{indicator} 변경 중…</> : '비밀번호 변경하기'}</button>
          </fieldset>
        </form>
        <button type="button" className="recovery-link" onClick={restart} disabled={Boolean(busy)}>본인 확인 다시 하기</button>
      </section> : <>
        <section className="recovery-card" aria-labelledby="recovery-identity-title">
          <h2 id="recovery-identity-title">가입 정보 확인</h2>
          <p className="recovery-hint">회원가입 때 입력한 본인 이름과 휴대전화 번호를 입력해 주세요.</p>
          <form onSubmit={requestCode} noValidate aria-busy={busy === 'request'}>
            <div className="recovery-field"><label htmlFor="recovery-name">이름</label><input id="recovery-name" name="name" autoComplete="name" value={identity.name} onChange={changeIdentity} maxLength={20} placeholder="가입한 이름" /></div>
            {purpose === 'password' && <div className="recovery-field"><label htmlFor="recovery-id">아이디</label><input id="recovery-id" name="id" autoComplete="username" autoCapitalize="none" spellCheck={false} value={identity.id} onChange={changeIdentity} maxLength={30} placeholder="가입한 아이디" /></div>}
            <div className="recovery-field"><label htmlFor="recovery-phone">휴대전화</label><input id="recovery-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" value={identity.phone} onChange={changeIdentity} maxLength={15} placeholder="01012345678" /></div>
            <button type="submit" className={challenge ? 'recovery-secondary' : 'recovery-primary'} disabled={Boolean(busy) || retrySeconds > 0}>
              {busy === 'request' ? <>{indicator} 인증번호 요청 중…</> : retrySeconds > 0 ? `${retrySeconds}초 후 재요청 가능` : challenge ? '인증번호 다시 받기' : '인증번호 받기'}
            </button>
          </form>
          <p className="recovery-hint recovery-contact-note">등록한 휴대전화를 사용할 수 없다면 선생님 또는 운영자에게 문의해 주세요. 학부모는 로그인 화면에서 휴대전화 인증으로 이용해 주세요.</p>
        </section>

        {challenge && <section className="recovery-card" aria-labelledby="recovery-verify-title">
          <div className="recovery-step-heading"><FiSmartphone aria-hidden="true" /><h2 id="recovery-verify-title" tabIndex={-1} ref={stepRef}>휴대전화 본인 확인</h2></div>
          <p className="recovery-hint">가입 정보가 일치하면 입력한 번호로 인증번호가 발송됩니다. 문자가 오지 않으면 가입 정보를 확인해 주세요.</p>
          <form onSubmit={verifyCode} noValidate aria-busy={busy === 'verify'}>
            <div className="recovery-field"><label htmlFor="recovery-otp">인증번호</label><div className="recovery-otp-field"><input id="recovery-otp" value={code} onChange={(event) => { setCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 6)); setError(''); }} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6자리 숫자" disabled={Boolean(busy) || !challengeSeconds} /><span className="recovery-timer" aria-label="인증번호 남은 시간">{clockText(challengeSeconds)}</span></div></div>
            {!challengeSeconds && <p className="recovery-hint">인증 시간이 만료되었습니다. 인증번호를 다시 받아 주세요.</p>}
            <button type="submit" className="recovery-primary" disabled={Boolean(busy) || !challengeSeconds}>{busy === 'verify' ? <>{indicator} 확인 중…</> : '인증번호 확인'}</button>
          </form>
        </section>}
      </>}
      <footer className="recovery-footer"><button type="button" className="recovery-link" disabled={busy === 'reset'} onClick={() => navigate('/login')}>로그인으로 돌아가기</button></footer>
    </div>
  </main>;
}
